import { Button, Divider, Popover } from '@arco-design/web-react';
import { IconPause, IconPhone, IconPlayArrow, IconSwap, IconUser } from '@arco-design/web-react/icon';
import IconMicOff from '../../../components/icons/IconMicOff';
import IconMicOn from '../../../components/icons/IconMicOn';
import IconHangup from '../../../components/icons/IconHangup';
import TransferPopover from './TransferPopover';

export interface QuickDialCallControlsProps {
  /** 软电话状态：ringing-in / ringing-out / in-call 决定出现哪些控件 */
  status: string;
  /** 接听中（SIP accept → 桥接有 ~2s 延迟）：按钮 loading 防连点 */
  answering: boolean;
  muted: boolean;
  held: boolean;
  transferVisible: boolean;
  onTransferVisibleChange: (v: boolean) => void;
  onAnswer: () => void;
  /** 取消外呼/拒接/挂断共用：入口侧先复位「刚完成盲转」标记再挂断 */
  onHangup: () => void;
  onToggleMute: () => void;
  onToggleHold: () => void;
  /** 常驻顶栏从 WS 采集的当前来电 uuid（振铃态后端受控转接用） */
  ringCallUUIDRef: { current: string };
  /** 盲转成功：入口侧标记本腿结束后不做话后快照 */
  onTransferred: () => void;
  /** 咨询转阶段：空=无第二路；ringing/up 时条上换成完成转接/取消 */
  consultPhase?: '' | 'ringing' | 'up';
  consultDest?: string;
  /** 仅 zitch 显示「邀请专家」 */
  canConsult?: boolean;
  onInviteVisibleChange: (v: boolean) => void;
  inviteVisible: boolean;
  onCompleteConsult: () => void;
  onCancelConsult: () => void;
  /** 已发出完成转接/盲转：本腿即将释放，不再给转接/邀请入口 */
  transferredOut?: boolean;
}

/**
 * 顶栏胶囊右侧的通话控件（取消外呼 / 接听 / 静音 / 保持 / 转接 / 拒接 / 挂断）。
 * 仅渲染与分发，通话动作由入口侧（软电话 store 驱动）传入。
 */
export default function QuickDialCallControls({
  status,
  answering,
  muted,
  held,
  transferVisible,
  onTransferVisibleChange,
  onAnswer,
  onHangup,
  onToggleMute,
  onToggleHold,
  ringCallUUIDRef,
  onTransferred,
  consultPhase = '',
  consultDest = '',
  canConsult = false,
  inviteVisible,
  onInviteVisibleChange,
  onCompleteConsult,
  onCancelConsult,
  transferredOut = false,
}: QuickDialCallControlsProps) {
  const inCall = status === 'in-call';
  const consulting = inCall && !!consultPhase && !transferredOut;
  return (
    /* 动作组自带容器：**不能**依赖外层 Arco Space 给间距——Space 把整个 fragment 当成一个 item
       （实测只包 2 项），组内 4 个按钮的相邻空隙会是 0px。间距统一由 .bcc-quick-dial-actions 的 gap 给。 */
    <div className="bcc-quick-dial-actions">
      <Divider type="vertical" className="bcc-quick-dial-sep" />
      {status === 'ringing-out' && (
        <Button
          size="small"
          status="danger"
          icon={<IconHangup className="arco-icon" />}
          onClick={onHangup}
          aria-label="取消外呼"
          title="取消外呼（号码拨错 / 不再呼叫）"
        >
          取消
        </Button>
      )}
      {status === 'ringing-in' && (
        <Button
          size="small"
          type="primary"
          status="success"
          loading={answering}
          disabled={answering}
          icon={<IconPhone />}
          onClick={onAnswer}
        >
          {answering ? '接听中' : '接听'}
        </Button>
      )}
      {inCall && (
        <>
          {/* 静音：默认开麦常色 + 开麦图标；静音后主色实底 + 关闭麦克风图标。
              为什么用主色而不是 danger 红：红色在本条里必须**独占**「挂断」语义——
              挂断钮描边与激活实底若同为 #EF4444，一行里三处红、最危险的挂断反而最不显眼。 */}
          <Button
            size="small"
            shape="circle"
            type={muted ? 'primary' : 'outline'}
            icon={muted ? <IconMicOff className="arco-icon" /> : <IconMicOn className="arco-icon" />}
            aria-label="静音"
            title={muted ? '取消静音' : '静音'}
            onClick={onToggleMute}
          />
          {/* 保持：默认常色 + 暂停图标；保持中主色实底 + 播放图标（点击恢复） */}
          <Button
            size="small"
            shape="circle"
            type={held ? 'primary' : 'outline'}
            icon={held ? <IconPlayArrow /> : <IconPause />}
            aria-label="通话保持"
            title={
              consulting
                ? '咨询中客户已保持，请完成转接或取消后再操作'
                : held
                  ? '恢复通话'
                  : '保持通话'
            }
            disabled={consulting || transferredOut}
            onClick={onToggleHold}
          />
        </>
      )}
      {/* 转接（技能组 / 坐席 / 话机 三选）：outline 与静音/保持同主题；
          通话中走 SIP REFER 盲转；来电振铃态（未接听）走 /cmd/transfer-call 受控转接。
          咨询进行中隐藏盲转，避免两套转接叠在一条上。 */}
      {!consulting && !transferredOut && (inCall || status === 'ringing-in') && (
        <Popover
          trigger="click"
          popupVisible={transferVisible}
          onVisibleChange={(v) => onTransferVisibleChange(v)}
          content={
            <TransferPopover
              onClose={() => onTransferVisibleChange(false)}
              disabled={!inCall && status !== 'ringing-in'}
              ringCallUUIDRef={ringCallUUIDRef}
              onTransferred={onTransferred}
            />
          }
          position="bl"
          className="bcc-strip-popover bcc-strip-transfer"
        >
          <Button
            size="small"
            type="outline"
            shape={status === 'ringing-in' ? 'square' : 'circle'}
            icon={<IconSwap />}
            aria-label="转接"
            title={status === 'ringing-in' ? '转接（未接听）' : '转接'}
          >
            {status === 'ringing-in' ? '转接' : null}
          </Button>
        </Popover>
      )}
      {/* 咨询转：与盲转共用目录，入口分开。未升会前接通后只能完成转接或取消。 */}
      {inCall && canConsult && !consulting && (
        <Popover
          trigger="click"
          popupVisible={inviteVisible}
          onVisibleChange={(v) => onInviteVisibleChange(v)}
          content={
            <TransferPopover
              intent="consult"
              onClose={() => onInviteVisibleChange(false)}
              disabled={false}
              ringCallUUIDRef={ringCallUUIDRef}
            />
          }
          position="bl"
          className="bcc-strip-popover bcc-strip-transfer"
        >
          <Button
            size="small"
            type="outline"
            icon={<IconUser />}
            aria-label="邀请专家"
            title="邀请专家（先保持客户，接通后完成转接或取消）"
          >
            邀请
          </Button>
        </Popover>
      )}
      {consulting && (
        <>
          {consultPhase === 'up' && (
            <Button
              size="small"
              type="primary"
              status="success"
              aria-label="完成转接"
              title={consultDest ? `完成转接给 ${consultDest}（自己退出；三方尚未提供）` : '完成转接（自己退出，客户与专家继续通话；三方尚未提供）'}
              onClick={onCompleteConsult}
            >
              完成转接
            </Button>
          )}
          <Button
            size="small"
            type="outline"
            aria-label="取消邀请"
            title={
              consultPhase === 'ringing'
                ? consultDest
                  ? `取消邀请 ${consultDest}，恢复与客户通话`
                  : '取消邀请，恢复与客户通话'
                : '取消邀请，挂断专家并恢复客户（三方尚未提供）'
            }
            onClick={onCancelConsult}
          >
            取消
          </Button>
        </>
      )}
      {status === 'ringing-in' && (
        <Button
          size="small"
          status="danger"
          icon={<IconHangup className="arco-icon" />}
          onClick={onHangup}
          aria-label="拒接"
          title="拒接"
        >
          拒接
        </Button>
      )}
      {inCall && !transferredOut && (
        <Button
          size="small"
          type="outline"
          shape="circle"
          status="danger"
          className="bcc-quick-dial-hangup"
          icon={<IconHangup className="arco-icon" />}
          onClick={onHangup}
          aria-label="挂断"
          title="挂断"
        />
      )}
    </div>
  );
}
